import { Component, OnInit } from '@angular/core';
import { Data } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { SessionState } from '@platform-frontend/account';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { LoadingState, MobileService } from '@platform-frontend/ui-tools';
import { CloseWindow, NotificationState, SimpleRouterState } from '@platform-frontend/util';
import { Observable, combineLatest, concat, of } from 'rxjs';
import { debounceTime, endWith, map, skipWhile, takeWhile } from 'rxjs/operators';
import { WebCheckoutIntegrationService } from './integration/web-checkout-integration.service';

@UntilDestroy()
@Component({
  selector: 'yat-web-checkout',
  templateUrl: './web-checkout.component.html',
  styleUrls: ['./web-checkout.component.scss']
})
export class WebCheckoutComponent implements OnInit {
  mobileService: MobileService;

  constructor(private store: Store, private webCheckoutIntegrationService: WebCheckoutIntegrationService, mobileService: MobileService) {
    this.mobileService = mobileService;
  }

  @Select(SimpleRouterState.data)
  routeData$: Observable<Data>;

  @Select(SessionState.isSignedIn)
  isSignedIn$: Observable<boolean>;

  @Select(NotificationState.hasNotifications)
  hasNotifications$: Observable<boolean>;

  showContent$: Observable<boolean>;
  header$: Observable<'simple' | 'academic' | 'commercial' | 'oAuth'>;
  showSkeleton$: Observable<boolean>;
  showNotifications$: Observable<boolean>;
  showBackdrop$: Observable<boolean>;
  applyContainer$: Observable<boolean>;
  headerExpanded$: Observable<boolean>;
  isStandaloneStartup$: Observable<boolean>;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  version = require('../../../../package.json').version;
  readonly environment = PLATFORM_ENVIRONMENT.name;
  readonly showEnvironmentLabel = !PLATFORM_ENVIRONMENT.isProduction;
  loadedStandalone = false;

  ngOnInit(): void {
    this.loadedStandalone = this.webCheckoutIntegrationService.getIntegrationMode() === 'standalone';
    this.isStandaloneStartup$ = this.loadedStandalone
      ? concat(
          of(true),
          this.store.select(LoadingState.isLoading).pipe(
            debounceTime(1),
            skipWhile(isLoading => !isLoading)
          )
        ).pipe(
          takeWhile(isLoading => isLoading),
          endWith(false)
        )
      : of(false);

    this.showContent$ = combineLatest([this.routeData$, this.webCheckoutIntegrationService.isRestartingOrShuttingDown()]).pipe(
      map(([data, isRestartingOrShuttingDown]) => !data.hidden && !isRestartingOrShuttingDown)
    );

    this.header$ = this.routeData$.pipe(map(data => data.header || 'simple'));

    this.headerExpanded$ = this.routeData$.pipe(map(data => data.headerExpanded));

    this.showSkeleton$ = this.store.select(LoadingState.isLoading);

    this.showNotifications$ = this.routeData$.pipe(map(data => data.notifications !== false));

    this.showBackdrop$ = combineLatest([this.routeData$, this.webCheckoutIntegrationService.isRestartingOrShuttingDown()]).pipe(
      map(([data, isRestartingOrShuttingDown]) => !data.hidden || isRestartingOrShuttingDown)
    );

    this.applyContainer$ = this.routeData$.pipe(map(data => data.container !== false));
  }

  closeApp(): void {
    this.store.dispatch(new CloseWindow());
  }

  showFooter(): boolean {
    return this.webCheckoutIntegrationService.getVendorType() === 'EXTERNAL';
  }
}
